/**
 * Core Tasks Query Hooks (Epic 2 — Kerntakencatalogus, Story 2.2)
 *
 * Eigen bestand (i.p.v. uitbreiding van `useSettings.ts`) — kerntaken is een
 * eigen, groeiend domein (Epic 3/4 bouwen erop voort), consistent met de
 * aanbeveling uit Story 2.1 voor `types/coreTasks.ts`/`schemas/coreTasks.ts`.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coreTasksApi } from '../../lib/coreTasksApi';
import type { CreateCoreTaskRequest, UpdateCoreTaskRequest } from '@functieloonhuis/shared';

export const coreTaskKeys = {
  all: ['coreTasks'] as const,
  lists: () => [...coreTaskKeys.all, 'list'] as const,
  list: (includeArchived: boolean) => [...coreTaskKeys.lists(), { includeArchived }] as const,
  details: () => [...coreTaskKeys.all, 'detail'] as const,
  detail: (id: number) => [...coreTaskKeys.details(), id] as const,
};

export function useCoreTasks(includeArchived = false) {
  return useQuery({
    queryKey: coreTaskKeys.list(includeArchived),
    queryFn: () => coreTasksApi.list(includeArchived),
  });
}

/**
 * Get-by-id hook — niet geconsumeerd binnen déze story's UI (de tabel + Sheet
 * werken met de al-geladen lijst), maar onderdeel van Story 2.2 Task 2's
 * expliciete hook-set en bedoeld voor hergebruik door een latere
 * detail-/deeplink-weergave (Epic 3/4). `enabled: id !== undefined` i.p.v.
 * `!!id` zodat een (hypothetische) id van `0` niet stilzwijgend de query
 * uitschakelt.
 */
export function useCoreTask(id: number | undefined) {
  return useQuery({
    queryKey: coreTaskKeys.detail(id!),
    queryFn: () => coreTasksApi.get(id!),
    enabled: id !== undefined,
  });
}

export function useCreateCoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCoreTaskRequest) => coreTasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.lists() });
    },
  });
}

export function useUpdateCoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateCoreTaskRequest }) => coreTasksApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.lists() });
    },
  });
}

export function useArchiveCoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => coreTasksApi.archive(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.lists() });
    },
  });
}

export function useReactivateCoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => coreTasksApi.reactivate(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: coreTaskKeys.lists() });
    },
  });
}
